import { useQuery as useQueryV3 } from "@apollo/client";
import { useQuery } from "@apollo/react-hooks";
import * as Sentry from "@sentry/react";
import { arrayOf, bool, func, string } from "prop-types";

import { FEATURE_FLAGS_QUERY as FEATURE_FLAGS_QUERY_V3 } from "./newQuery";
import { FEATURE_FLAGS_QUERY } from "./query";

const useFeatureFlagsQuery = (usev3, params) => {
  if (usev3) {
    return useQueryV3(FEATURE_FLAGS_QUERY_V3, params);
  }
  return useQuery(FEATURE_FLAGS_QUERY, params);
};

export const useFeatureFlags = (names, usev3, actorContext, actorContextID) => {
  const { data, error, loading } = useFeatureFlagsQuery(usev3, {
    variables: {
      input: {
        names,
        actor_context: actorContext,
        actor_context_id: actorContextID
      }
    }
  });

  if (error || loading) {
    return {
      error,
      loading
    };
  }

  const flags = data.feature_flags.reduce(
    (result, flag) => ({
      ...result,
      [flag.name]: flag.enabled
    }),
    {}
  );

  return {
    flags
  };
};

export default function FeatureFlags({
  actorContext,
  actorContextID,
  children,
  names,
  usev3
}) {
  const { error, flags, loading } = useFeatureFlags(
    names,
    usev3,
    actorContext,
    actorContextID
  );

  if (loading) {
    return null;
  }

  if (error) {
    Sentry.captureException(error);
    return null;
  }

  return children({ flags });
}

FeatureFlags.propTypes = {
  /** Function for rendering children that returns the value of each feature flag */
  children: func,
  /** Array of feature flag names to be checked */
  names: arrayOf(string),
  usev3: bool
};

FeatureFlags.defaultProps = {
  children: null,
  names: [],
  usev3: false
};
